import React, { useState, useEffect, useRef, useMemo } from "react";
import "./ListView.scss";
import { can, PERM, normalizeRole, ROLES } from "../../config/roles";
import InfoButton from "../InfoButton/InfoButton";
import DetailsModal from "../DetailsModal/DetailsModal";
import AddButton from "../AddButton"; // or correct path
import AddTodoModal from "../Update/AddTodoModal/AddTodoModal";
import AddChecklistModal from "../Update/AddChecklistModal/AddChecklistModal";
import TooltipPortal from "../TooltipPortal/TooltipPortal";
import DeleteButton from "../DeleteButton/DeleteButton";
import DeleteWarningModal from "../DeleteWarningModal/DeleteWarningModal";
import notaskImg from "../../assets/notask.png"; // ✅ add this line

const ListView = ({
  tasks = [],
  stages = [],
  onToggleChecklist,
  onChangeTodoStage,
  onChangeTaskStage,
  canUpdateTodoList,
  currentUserRole,
  currentAccessId,
  projectMembers,
  currentEmpId,
  onDeleteTask,
  onDeleteTodoList,
  handleUpdateTask,
  handleUpdateTodo,
  onUpdateTodoList,
  projectEndDate,
  onCreateTodo,
  onCreateTodoList,
  onDeleteTodo,
  onUpdateChecklistRemark,
}) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [localTasks, setLocalTasks] = useState(tasks || []);
  const inFlightChecklistRef = useRef(new Set());
  const inFlightTaskStageRef = useRef(new Set());
  const [openTodos, setOpenTodos] = useState(new Set());
  const [openTasks, setOpenTasks] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'task'|'todo'|'checklist', id }

  // selected item for DetailsModal
  const [selectedItem, setSelectedItem] = useState(null);
  const [todoModal, setTodoModal] = useState({
    open: false,
    taskId: null,
    stagePositionId: null,
  });
  const [checkModal, setCheckModal] = useState({ open: false, toDoId: null });

  // ---------- Stage change remark prompt ----------
  // payload shape:
  //   { kind: "task", taskId, newStageId, previous }
  //   { kind: "todo", todoId, newStageId, previous }
  const [stageRemarkPrompt, setStageRemarkPrompt] = useState(null);
  const [stageRemarkText, setStageRemarkText] = useState("");

  // ---------- Checklist remark / approve ----------
  const [checklistRemarkPrompt, setChecklistRemarkPrompt] = useState(null); // { toDoListId, title, completion }
  const [checklistRemarkText, setChecklistRemarkText] = useState("");

  const openChecklistRemarkPrompt = (li) => {
    if (!li) return;
    setChecklistRemarkText(li.remark || "");
    setChecklistRemarkPrompt({
      toDoListId: li.toDoListId ?? li.id,
      title: li.toDoListTitle ?? "Checklist item",
      completion: !!li.completion,
    });
  };

  const cancelChecklistRemarkPrompt = () => {
    setChecklistRemarkPrompt(null);
    setChecklistRemarkText("");
  };

  const confirmChecklistRemarkPrompt = async () => {
    if (!checklistRemarkPrompt) return;
    const { toDoListId, completion } = checklistRemarkPrompt;
    try {
      await onUpdateChecklistRemark?.(toDoListId, {
        completionFlag: completion,
        remark: checklistRemarkText,
      });
    } finally {
      setChecklistRemarkPrompt(null);
      setChecklistRemarkText("");
    }
  };

  const handleApproveChecklistItem = (li) => {
    const toDoListId = li?.toDoListId ?? li?.id;
    if (!toDoListId) return;
    onUpdateChecklistRemark?.(toDoListId, {
      completionFlag: true,
      remark: li?.remark ?? "",
    });
  };

  const openStageRemarkPrompt = (payload) => {
    setStageRemarkText("");
    setStageRemarkPrompt(payload);
  };

  const commitTaskStageRemark = async (
    taskId,
    newStageId,
    previous,
    remark,
  ) => {
    const trimmedRemark = remark && remark.trim() ? remark.trim() : undefined;
    try {
      await onChangeTaskStage(taskId, newStageId, trimmedRemark);
    } catch (err) {
      console.error("Stage update failed", err);
      setLocalTasks(previous);
    } finally {
      inFlightTaskStageRef.current.delete(String(taskId));
    }
  };

  const commitTodoStageRemark = async (
    todoId,
    newStageId,
    previous,
    remark,
  ) => {
    const trimmedRemark = remark && remark.trim() ? remark.trim() : undefined;
    try {
      await onChangeTodoStage(todoId, newStageId, trimmedRemark);
    } catch (err) {
      console.error("Stage update failed:", err);
      setLocalTasks(previous);
    }
  };

  const confirmStageRemark = () => {
    if (!stageRemarkPrompt) return;
    const { kind, taskId, todoId, newStageId, previous } = stageRemarkPrompt;
    if (kind === "task") {
      commitTaskStageRemark(taskId, newStageId, previous, stageRemarkText);
    } else if (kind === "todo") {
      commitTodoStageRemark(todoId, newStageId, previous, stageRemarkText);
    }
    setStageRemarkPrompt(null);
    setStageRemarkText("");
  };

  const cancelStageRemark = () => {
    if (!stageRemarkPrompt) return;
    const { kind, taskId, previous } = stageRemarkPrompt;
    setLocalTasks(previous);
    if (kind === "task") inFlightTaskStageRef.current.delete(String(taskId));
    setStageRemarkPrompt(null);
    setStageRemarkText("");
  };

  // openDetailsModal: copy the Kanban implementation (keeps behaviour identical)
  // openDetailsModal — normalized like TableView / Kanban
  const openDetailsModal = (item, parentTask = null) => {
    const it = { ...(item ?? {}) };

    // Checklist item branch
    if (it?.toDoListId || (it?.id && it?.toDoListTitle)) {
      setSelectedItem({
        ...it,
        isChecklist: true,
        __parent: parentTask ?? it.__parent ?? null,
      });
      return;
    }

    const parentOwner =
      parentTask?.taskOwnerId ??
      parentTask?.ownerId ??
      it?.parentTaskOwnerId ??
      it?.parentOwnerId ??
      (it?.__parent && (it.__parent.taskOwnerId ?? it.__parent.ownerId)) ??
      null;

    const getTodoOwnerFrom = (o) =>
      o?.assignedToEmpId ??
      o?.assignedEmpId ??
      o?.assigneeId ??
      o?.assigned_to_emp_id ??
      o?.assignee_emp_id ??
      o?.empId ??
      o?.userId ??
      o?.assignedTo ??
      o?.ownerId ??
      null;

    // ToDo branch -> normalize to `userId`
    if (it?.toDoId || it?.toDoId === 0) {
      const todoOwner = getTodoOwnerFrom(it);
      setSelectedItem({
        ...it,
        // TableView expects `userId` for ToDo owner
        userId: todoOwner,
        parentTaskOwnerId: parentOwner,
        __parent: parentTask ?? it.__parent ?? null,
      });
      return;
    }

    // Task branch -> normalize to `taskOwnerId`
    if (it?.taskId || it?.id) {
      const taskOwnerId =
        it.taskOwnerId ??
        it.ownerId ??
        it.taskOwnerEmpId ??
        it.createdEmpId ??
        null;
      setSelectedItem({
        ...it,
        taskOwnerId,
        parentTaskOwnerId: parentOwner,
        __parent: parentTask ?? it.__parent ?? null,
      });
      return;
    }

    // fallback
    setSelectedItem({
      ...it,
      parentTaskOwnerId: parentOwner,
      __parent: parentTask ?? it.__parent ?? null,
    });
  };

  const role = normalizeRole(currentUserRole);
  // prefer employee id (used for assignedEmpId/assigned_to_emp_id), fall back to access id
  const currentUserId = String(
    currentEmpId ?? currentAccessId ?? projectMembers?.currentUserId ?? "",
  );

  // --- Role / ownership helpers ---
  const getTaskOwnerId = (task) =>
    task?.taskOwnerId ??
    task?.ownerId ??
    task?.taskOwnerEmpId ??
    task?.createdEmpId ??
    task?.createdBy ??
    null;

  const getTodoOwnerId = (todo) =>
    todo?.assignedEmpId ??
    todo?.empId ??
    todo?.assigneeId ??
    todo?.assignedToEmpId ??
    null;

  const isTaskOwner = (task, userId) =>
    userId != null && String(getTaskOwnerId(task)) === String(userId);

  const isTodoOwner = (todo, userId) =>
    userId != null && String(getTodoOwnerId(todo)) === String(userId);

  // ✅ Whether current user can update a Task stage
  const canChangeTaskStage = (task, role, userId) => {
    if (!can(role, PERM.TASK_UPDATE)) return false;
    if (role === ROLES.CREATOR || role === ROLES.APPROVER) return true;
    return isTaskOwner(task, userId);
  };

  // ✅ Whether current user can update a Todo stage
  const canChangeTodoStage = (todo, parentTask, role, userId) => {
    if (!can(role, PERM.TODO_UPDATE)) return false;
    if (role === ROLES.CREATOR || role === ROLES.APPROVER) return true;

    if (role === ROLES.DOER) {
      // Doer can only change ToDo if assigned OR owns the parent task
      if (isTodoOwner(todo, userId)) return true;
      if (isTaskOwner(parentTask, userId)) return true;
      return false;
    }
    return false;
  };

  const isChecklistItemDone = (li) =>
    !!(li?.completion ?? li?.completionFlag) ||
    (li?.status && String(li.status).toUpperCase() === "DONE");

  const isTodoDone = (todo) => {
    const lists = todo?.toDoListResponseDTOList || [];
    return lists.length > 0 && lists.every(isChecklistItemDone);
  };

  const isTaskCompletable = (task) => {
    const todos = task?.todoRequestDTOList || [];
    return todos.length > 0 && todos.every(isTodoDone);
  };

  const handleTaskStageChange = async (taskId, newStageId) => {
    const task = localTasks.find((t) => t.taskId === taskId);
    if (!task) return;

    const stage = stages.find((s) =>
      s.stagePositionResponseDTOList?.some(
        (pos) => Number(pos.stagePositionId) === newStageId,
      ),
    );

    const flag = stage?.stageFlag ?? stage?.flag ?? "";
    const isFinal = flag && String(flag).toLowerCase().includes("final");

    if (isFinal && !isTaskCompletable(task)) {
      console.warn("Blocked: Cannot move task to final stage");
      return;
    }

    const previous = localTasks;
    inFlightTaskStageRef.current.add(String(taskId));
    setLocalTasks((prev) =>
      prev.map((t) =>
        t.taskId === taskId ? { ...t, stagePositionId: newStageId } : t,
      ),
    );

    openStageRemarkPrompt({ kind: "task", taskId, newStageId, previous });
  };

  useEffect(() => {
    setLocalTasks((prev = []) => {
      if (!prev || prev.length === 0) return tasks || [];

      const prevById = new Map(prev.map((t) => [String(t.taskId ?? t.id), t]));

      const merged = (tasks || []).map((serverT) => {
        const id = String(serverT.taskId ?? serverT.id ?? "");
        const prevTask = prevById.get(id);
        if (!prevTask) return serverT;

        const prevTodosById = new Map(
          (prevTask.todoRequestDTOList || []).map((td) => [
            String(td.toDoId ?? td.id),
            td,
          ]),
        );

        const mergedTodos = (serverT.todoRequestDTOList || []).map((st) => {
          const tid = String(st.toDoId ?? st.id);
          const prevTodo = prevTodosById.get(tid) ?? st;

          const prevListsById = new Map(
            (prevTodo.toDoListResponseDTOList || []).map((li) => [
              String(li.toDoListId ?? li.id),
              li,
            ]),
          );

          const mergedLists = (st.toDoListResponseDTOList || []).map(
            (srvLi) => {
              const lid = String(srvLi.toDoListId ?? srvLi.id);
              if (inFlightChecklistRef.current.has(lid)) {
                return prevListsById.get(lid) ?? srvLi;
              }
              return srvLi;
            },
          );

          return { ...st, toDoListResponseDTOList: mergedLists };
        });

        return { ...serverT, todoRequestDTOList: mergedTodos };
      });

      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);
  useEffect(() => {
    const sample = (localTasks || tasks || []).slice(0, 5);
    console.group("[ListView] ownership debug");
    console.log(
      "currentEmpId",
      currentEmpId,
      "currentAccessId",
      currentAccessId,
      "currentUserId",
      currentUserId,
      "role",
      role,
    );
    sample.forEach((task, ti) => {
      const tId = task.taskId ?? task.id ?? `i${ti}`;
      console.groupCollapsed(`task ${tId}`);
      console.log("task obj", task);
      console.log("getTaskOwnerId:", getTaskOwnerId(task));
      (task.todoRequestDTOList || []).forEach((todo, idx) => {
        console.groupCollapsed(` todo #${idx} ${todo.toDoId ?? todo.id}`);
        console.log("todo obj", todo);
        console.log("getTodoOwnerId:", getTodoOwnerId(todo));
        const todoOwner = String(getTodoOwnerId(todo) ?? "");
        console.log(
          "matches currentUserId?",
          todoOwner === String(currentUserId),
          { todoOwner, currentUserId },
        );
        console.groupEnd();
      });
      console.groupEnd();
    });
    console.groupEnd();
  }, [tasks, localTasks, currentEmpId, currentAccessId, currentUserId, role]);

  const stageOptions = useMemo(() => {
    const byId = new Map();
    for (const s of stages || []) {
      for (const pos of s.stagePositionResponseDTOList || []) {
        const id = String(pos.stagePositionId ?? pos.id ?? "");
        if (!id || byId.has(id)) continue;
        const label =
          s.stageName ??
          s.stageTitle ??
          s.title ??
          `Stage ${pos.stagePositionId}`;
        byId.set(id, { value: pos.stagePositionId, label });
      }
    }
    return Array.from(byId.values());
  }, [stages]);

  const updateLocalChecklist = (listId, nextChecked) => {
    const lid = String(listId);
    console.log("[ListView] updateLocalChecklist", {
      listId: lid,
      nextChecked,
    });
    setLocalTasks((prev = []) =>
      prev.map((task) => {
        const todos = (task.todoRequestDTOList || []).map((todo) => {
          const lists = (todo.toDoListResponseDTOList || []).map((li) =>
            String(li.toDoListId ?? li.id) === lid
              ? { ...li, completion: nextChecked }
              : li,
          );
          return { ...todo, toDoListResponseDTOList: lists };
        });
        return { ...task, todoRequestDTOList: todos };
      }),
    );
  };

  const handleLocalChecklistToggle = async (todoId, listId, checked) => {
    const lid = String(listId);
    console.log("[ListView] handleLocalChecklistToggle START", {
      todoId,
      listId: lid,
      checked,
      inFlightBefore: Array.from(inFlightChecklistRef.current),
    });
    const prevSnapshot = localTasks;
    inFlightChecklistRef.current.add(lid);
    updateLocalChecklist(lid, checked);
    setRefreshKey((k) => k + 1);

    try {
      if (!onToggleChecklist) {
        console.warn("[ListView] onToggleChecklist not provided");
      }
      const result = onToggleChecklist?.(listId, checked);
      // log the returned value / promise
      console.log("[ListView] onToggleChecklist returned", result);
      if (result && typeof result.then === "function") {
        await result;
      }
      console.log("[ListView] handleLocalChecklistToggle SUCCESS", {
        listId: lid,
      });
    } catch (err) {
      setLocalTasks(prevSnapshot);
      console.error("[ListView] Checklist update failed (reverted)", err);
    } finally {
      inFlightChecklistRef.current.delete(lid);
      setRefreshKey((k) => k + 1);
      console.log("[ListView] handleLocalChecklistToggle END", {
        listId: lid,
        inFlightAfter: Array.from(inFlightChecklistRef.current),
      });
    }
  };

  // ---------- delete permission helpers (TableView parity) ----------
  const canDeleteTaskFor = (task) => {
    const r = role;
    if (!can(r, PERM.TASK_DELETE)) return false;

    const ownerId = getTaskOwnerId(task);
    const isOwner =
      currentUserId && ownerId && String(ownerId) === String(currentUserId);

    if (r === ROLES.CREATOR || r === ROLES.APPROVER) return true;
    if (r === ROLES.DOER && isOwner) return true;
    return false;
  };

  const canDeleteTodoFor = (todo, parentTask = null) => {
    const r = role;
    if (!can(r, PERM.TODO_DELETE)) return false;

    const todoOwnerId = getTodoOwnerId(todo);
    const taskOwnerId = getTaskOwnerId(parentTask ?? {});
    const isTodoOwner =
      currentUserId &&
      todoOwnerId &&
      String(todoOwnerId) === String(currentUserId);
    const isTaskOwnerLocal =
      currentUserId &&
      taskOwnerId &&
      String(taskOwnerId) === String(currentUserId);

    if (r === ROLES.CREATOR || r === ROLES.APPROVER) return true;
    if (r === ROLES.DOER && (isTodoOwner || isTaskOwnerLocal)) return true;
    return false;
  };
  // ---------- end delete helpers ----------

  const handleTodoStageChange = async (todoId, newStageId) => {
    const previous = localTasks;
    setLocalTasks((prev) =>
      prev.map((task) => ({
        ...task,
        todoRequestDTOList: task.todoRequestDTOList?.map((todo) =>
          todo.toDoId === todoId
            ? { ...todo, stagePositionId: newStageId }
            : todo,
        ),
      })),
    );

    openStageRemarkPrompt({ kind: "todo", todoId, newStageId, previous });
  };
  if (!localTasks || localTasks.length === 0) {
    return (
      <div className="list-view090 list-view090--empty">
        <img
          src={notaskImg}
          alt="No tasks available"
          // style={{ width: "300px", maxWidth: "90%" }}
        />
      </div>
    );
  }

  return (
    <div key={refreshKey} className="list-view090">
      {localTasks.map((task, tIndex) => {
        const taskKey = String(task?.taskId ?? task?.id ?? `i${tIndex}`);

        const title = task.taskTitle ?? "Untitled Task";
        const description = task.description ?? "No description provided";
        const owner = task.taskOwnerName ?? "Unassigned";
        const priority = task.priority ?? "Medium";
        const todos = task.todoRequestDTOList || [];

        return (
          <article key={taskKey} className="task-block090">
            <div className="task-header">
              <div className="task-header__top">
                <div className="task-header__left">
                  <span className="task-header__label">Stages </span>
                  <h2 className="task-header__title">{title}</h2>
                  <p className="task-header__desc">{description}</p>
                </div>

                <div className="task-header__right">
                  <div className="task-header__meta">
                    <div className="owner-badge">
                      <span className="owner-avatar">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="#0088ffff"
                        >
                          <path
                            d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 
                       1.79-4 4 1.79 4 4 4zm0 2c-2.67 
                       0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                          />
                        </svg>
                      </span>
                      <span className="owner-name">{owner}</span>
                    </div>

                    <span
                      className={`priority-badge ${String(
                        priority || "medium",
                      ).toLowerCase()}`}
                    >
                      {priority || "Medium"}
                    </span>

                    <span className="add-task">
                      {/* Add new To-Do button (task-level) */}
                      <div
                        className="task-header__add-todo"
                        style={{
                          marginTop: "6px",
                        }}
                      >
                        <AddButton
                          size={30}
                          onClick={() => {
                            const canAddTodo =
                              role === "CREATOR" ||
                              role === "APPROVER" ||
                              (role === "DOER" &&
                                isTaskOwner(task, currentUserId)); // reuse helper
                            if (!canAddTodo) {
                              // you can keep a toast or console.log
                              console.info("Add To-Do blocked: not allowed");
                              return;
                            }
                            setTodoModal({
                              open: true,
                              taskId: task.taskId ?? task.id,
                              stagePositionId: task.stagePositionId ?? null,
                            });
                          }}
                          tooltip="Add new Task"
                          ariaLabel={`Add new ToDo for task ${taskKey}`}
                        />
                      </div>
                    </span>
                    {/* Info button for task */}
                    <span
                      className="info-btn090-wrap"
                      role="button"
                      tabIndex={0}
                      onClick={() => openDetailsModal(task)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openDetailsModal(task);
                        }
                      }}
                    >
                      <InfoButton />
                      {/* Task Delete (gated like TableView) */}
                    </span>
                    <span>
                      <TooltipPortal
                        label={
                          canDeleteTaskFor(task)
                            ? "Delete this Task (cannot be undone)"
                            : "You don't have permission to delete this Task"
                        }
                        offsetY={28}
                      >
                        {canDeleteTaskFor(task) ? (
                          <DeleteButton
                            onClick={() =>
                              setDeleteTarget({
                                type: "task",
                                id: task.taskId ?? task.id,
                              })
                            }
                            title="Delete Task"
                            size={43}
                          />
                        ) : (
                          <button
                            type="button"
                            disabled
                            title="You don't have permission to delete this Task"
                            className="disabled-delete-btn"
                            style={{
                              background: "transparent",
                              border: "none",
                              cursor: "not-allowed",
                              opacity: 0.6,
                              padding: 0,
                              marginLeft: 8,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            aria-hidden="true"
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              aria-hidden
                            >
                              <path
                                d="M3 6h18"
                                stroke="#999"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                              <path
                                d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6"
                                stroke="#999"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M10 11v6M14 11v6"
                                stroke="#999"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        )}
                      </TooltipPortal>
                    </span>
                  </div>
                </div>
              </div>
              <div className="arrow-for-todo"></div>

              <div className="task-header__stages-row">
                {/* 🟢 Expand/Collapse Arrow */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();

                    // compute ids of this task’s todos (normalized to strings)
                    const todoIds = (task?.todoRequestDTOList || [])
                      .map((td) => String(td?.toDoId ?? td?.id))
                      .filter(Boolean);

                    // flip per-task open flag
                    const isOpen = openTasks.has(taskKey);
                    setOpenTasks((prev) => {
                      const next = new Set(prev);
                      if (isOpen) next.delete(taskKey);
                      else next.add(taskKey);
                      return next;
                    });

                    // open/close all child todo cards for THIS task
                    setOpenTodos((prev) => {
                      const next = new Set(prev);
                      if (isOpen) {
                        // collapsing: remove this task’s todo ids
                        todoIds.forEach((id) => next.delete(id));
                      } else {
                        // expanding: add this task’s todo ids
                        todoIds.forEach((id) => next.add(id));
                      }
                      console.log("[Task Arrow]", {
                        taskKey,
                        isOpenBeforeClick: isOpen,
                        todoIds,
                        openTodosSizeAfter: next.size,
                      });
                      return next;
                    });
                  }}
                  className={`toggle-arrow-btn12 ${
                    openTasks.has(taskKey) ? "is-open" : ""
                  }`}
                  aria-expanded={openTasks.has(taskKey)}
                  aria-controls={`todo-grid-${taskKey}`}
                  aria-label={
                    openTasks.has(taskKey)
                      ? "Collapse task To-Dos"
                      : "Expand task To-Dos"
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {openTasks.has(taskKey) ? (
                      // ▼ (down arrow when open)
                      <polyline points="6 9 12 15 18 9" />
                    ) : (
                      // ▶ (right arrow when closed)
                      <polyline points="9 6 15 12 9 18" />
                    )}
                  </svg>
                </button>

                {/* Stages moved to right side */}
                <div className="task-header__stages">
                  {stageOptions.map((stage) => {
                    const isActive =
                      Number(task.stagePositionId) === Number(stage.value);
                    return (
                      <button
                        key={stage.value}
                        className={`stage-pill090 ${
                          isActive ? "is-active" : ""
                        }`}
                        disabled={
                          !canChangeTaskStage(task, role, currentUserId) ||
                          inFlightTaskStageRef.current.has(String(task.taskId))
                        }
                        onClick={() => {
                          if (!canChangeTaskStage(task, role, currentUserId))
                            return;
                          handleTaskStageChange(task.taskId, stage.value);
                        }}
                      >
                        {stage.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* TODOS */}
            {todos.length > 0 ? (
              <div className="todo-card-grid" id={`todo-grid-${taskKey}`}>
                {todos.map((todo, index) => (
                  <div
                    key={todo.toDoId}
                    className={`todo-card ${
                      openTodos.has(String(todo.toDoId ?? todo.id))
                        ? "is-open"
                        : ""
                    }`}
                  >
                    {/* --- Header --- */}
                    <div className="todo-card__header">
                      <div className="todo-card__label-wrap">
                        <span className="todo-card__index">#{index + 1}</span>
                        <span className="todo-card__label">
                          Task
                          <span
                            className="info-btn090-wrap"
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetailsModal(todo, task);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openDetailsModal(todo, task);
                              }
                            }}
                          >
                            <InfoButton />
                            {/* ToDo Delete (gated like TableView) */}
                          </span>
                          <span style={{ paddingTop: "5px" }}>
                            {can(role, PERM.TODOLIST_UPDATE) && (
                              <AddButton
                                size={30}
                                tooltip="Add checklist item"
                                ariaLabel={`Add checklist to todo ${
                                  todo.toDoId ?? todo.id
                                }`}
                                onClick={() =>
                                  setCheckModal({
                                    open: true,
                                    toDoId: todo.toDoId ?? todo.id,
                                  })
                                }
                              />
                            )}
                          </span>
                          <span style={{ display: "-webkit-inline-flex" }}>
                            <TooltipPortal
                              label={
                                canDeleteTodoFor(todo, task)
                                  ? "Delete this ToDo (cannot be undone)"
                                  : "You don't have permission to delete this ToDo"
                              }
                              offsetY={28}
                            >
                              {canDeleteTodoFor(todo, task) ? (
                                <DeleteButton
                                  onClick={(e) => {
                                    e.stopPropagation(); // keep existing behavior to avoid collapsing the card
                                    setDeleteTarget({
                                      type: "todo",
                                      id: todo.toDoId ?? todo.id,
                                      stopPropagation: true,
                                    });
                                  }}
                                  title="Delete ToDo"
                                  size={43}
                                />
                              ) : (
                                <button
                                  type="button"
                                  disabled
                                  title="You don't have permission to delete this ToDo"
                                  className="disabled-delete-btn"
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "not-allowed",
                                    opacity: 0.6,
                                    padding: 0,
                                    marginLeft: 8,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  aria-hidden="true"
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    aria-hidden
                                  >
                                    <path
                                      d="M3 6h18"
                                      stroke="#999"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6"
                                      stroke="#999"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </button>
                              )}
                            </TooltipPortal>
                          </span>
                          {/* Add checklist item button (todo-level) */}
                        </span>
                      </div>
                      <h3 className="todo-card__title">
                        {todo.toDoTitle}
                        {todo.remark && (
                          <span
                            className="todo-remark-icon"
                            style={{
                              marginLeft: 6,
                              fontSize: 12,
                              color: "#6b7280",
                              cursor: "help",
                            }}
                            title={todo.remark}
                          >
                            📝
                          </span>
                        )}
                      </h3>
                    </div>

                    {/* ✅ Collapsible Section (kept mounted for animation) */}
                    <div
                      className={`todo-card__content ${
                        openTodos.has(String(todo.toDoId ?? todo.id))
                          ? "is-open"
                          : ""
                      }`}
                    >
                      {/* --- Meta Info --- */}
                      <div className="todo-card__meta">
                        {todo.remark && (
                          <p className="todo-card__desc">
                            <strong>Remark:</strong> {todo.remark}
                          </p>
                        )}
                        <div className="owner-badge">
                          <span className="owner-avatar">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="#1877ebff"
                            >
                              <path
                                d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 
                1.79-4 4 1.79 4 4 4zm0 2c-2.67 
                0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                              />
                            </svg>
                          </span>
                          <span className="owner-name">
                            {todo.assignedEmpName || "Unassigned"}
                          </span>
                        </div>

                        <span
                          className={`priority-badge ${String(
                            todo.priority || "medium",
                          ).toLowerCase()}`}
                        >
                          {todo.priority || "Medium"}
                        </span>
                      </div>

                      {/* --- Stages --- */}
                      <div className="todo-card__stages">
                        <span className="section-label090">Status</span>
                        <div className="todo-stages090__pills">
                          {stageOptions.map((stage) => {
                            const isActive =
                              Number(todo.stagePositionId) ===
                              Number(stage.value);
                            return (
                              <button
                                key={stage.value}
                                className={`stage-pill090 ${
                                  isActive ? "is-active" : ""
                                }`}
                                disabled={
                                  !canChangeTodoStage(
                                    todo,
                                    task,
                                    role,
                                    currentUserId,
                                  )
                                }
                                onClick={() => {
                                  if (
                                    !canChangeTodoStage(
                                      todo,
                                      task,
                                      role,
                                      currentUserId,
                                    )
                                  )
                                    return;
                                  handleTodoStageChange(
                                    todo.toDoId,
                                    stage.value,
                                  );
                                }}
                              >
                                {stage.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* --- Checklist --- */}
                      <div className="todo-card__checklist-container">
                        <div className="todo-card__checklist">
                          <span className="section-label090">
                            Todo/TodoList
                          </span>
                          {todo.toDoListResponseDTOList?.length > 0 ? (
                            <ul className="checklist-list090">
                              {todo.toDoListResponseDTOList.map((li) => {
                                const listId = li.toDoListId ?? li.id;
                                return (
                                  <li key={listId}>
                                    <span
                                      className="info-btn090-wrap"
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => openDetailsModal(li, todo)}
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === "Enter" ||
                                          e.key === " "
                                        ) {
                                          e.preventDefault();
                                          openDetailsModal(li, todo);
                                        }
                                      }}
                                    >
                                      <InfoButton />
                                    </span>

                                    {/* DEBUG: temporary native checkbox to verify events */}
                                    <div className="checklist-item090__body">
                                      <label className="checklist-label">
                                        <input
                                          className="checklist-input"
                                          type="checkbox"
                                          checked={!!li.completion}
                                          onChange={(e) =>
                                            handleLocalChecklistToggle(
                                              todo.toDoId,
                                              li.toDoListId ?? li.id,
                                              e.target.checked,
                                            )
                                          }
                                          aria-label={
                                            li.toDoListTitle ?? "Checklist item"
                                          }
                                        />
                                        <span className="checklist-title">
                                          {li.toDoListTitle ?? "Untitled Item"}
                                        </span>
                                      </label>

                                      {li.remark ? (
                                        <span
                                          className="checklist-item-remark"
                                          title={li.remark}
                                        >
                                          Remark: {li.remark}
                                        </span>
                                      ) : null}

                                      <div className="checklist-item-actions">
                                        <button
                                          type="button"
                                          className="checklist-remark-btn"
                                          onClick={() =>
                                            openChecklistRemarkPrompt(li)
                                          }
                                        >
                                          {li.remark
                                            ? "Edit Remark"
                                            : "Add Remark"}
                                        </button>
                                        {li.completion ? (
                                          <span className="checklist-approved-badge">
                                            Approved
                                          </span>
                                        ) : (
                                          <button
                                            type="button"
                                            className="checklist-approve-btn"
                                            onClick={() =>
                                              handleApproveChecklistItem(li)
                                            }
                                          >
                                            Approve
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <p className="empty-text090">No checklist items</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-message">No Tasks available</div>
            )}
          </article>
        );
      })}
      {selectedItem && (
        <DetailsModal
          item={selectedItem}
          currentUserRole={currentUserRole}
          currentEmpId={currentEmpId}
          // pass projectMembers so modal can resolve empId/accessId -> member objects
          projectMembers={projectMembers}
          // keep both forms of current user id (accessId fallback)
          currentUserId={currentAccessId ?? currentEmpId}
          onDeleteTask={onDeleteTask}
          onUpdateTodoList={onUpdateTodoList}
          onDeleteTodoList={onDeleteTodoList}
          parentTaskOwnerId={
            selectedItem?.parentTaskOwnerId ??
            selectedItem?.parentOwnerId ??
            selectedItem?.__parent?.taskOwnerId ??
            null
          }
          projectEndDate={projectEndDate}
          onClose={() => setSelectedItem(null)}
          onSave={(item, formData) => {
            if (item.taskId) {
              return handleUpdateTask?.(item.taskId, {
                taskTitle: formData.title,
                description: formData.description,
                remark: formData.remark,
                priority: formData.priority,
                startDate: formData.startDate,
                endDate: formData.endDate,
                schedulingMode: formData.schedulingMode,
                durationDays: formData.durationDays,
              });
            }
            if (item.toDoId) {
              const titleValue = formData.title ?? formData.toDoTitle ?? "";
              return handleUpdateTodo?.(item.toDoId, {
                toDoTitle: titleValue,
                remark: formData.remark,
              });
            }
            if (item.toDoListId) {
              const nextTitle = formData.toDoListTitle ?? formData.title ?? "";
              return onUpdateTodoList?.(item.toDoListId, {
                toDoListTitle: nextTitle,
              });
            }
            return Promise.resolve();
          }}
        />
      )}

      {/* Add Todo Modal */}
      <AddTodoModal
        open={todoModal.open}
        onClose={() =>
          setTodoModal({ open: false, taskId: null, stagePositionId: null })
        }
        taskId={todoModal.taskId}
        stagePositionId={todoModal.stagePositionId}
        onCreateTodo={onCreateTodo}
        accessId={currentAccessId}
        projectMembers={projectMembers}
        currentUserRole={currentUserRole}
      />

      {/* Add Checklist Modal */}
      <AddChecklistModal
        open={checkModal.open}
        onClose={() => setCheckModal({ open: false, toDoId: null })}
        toDoId={checkModal.toDoId}
        onCreateTodoList={onCreateTodoList}
      />

      {stageRemarkPrompt && (
        <div
          className="stage-remark-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) cancelStageRemark();
          }}
        >
          <div className="stage-remark-modal">
            <h4>Add a remark</h4>
            <p className="stage-remark-sub">
              {stageRemarkPrompt.kind === "task"
                ? "Updating Task stage"
                : "Updating ToDo stage"}
            </p>
            <textarea
              autoFocus
              rows={3}
              placeholder="Optional remark for this stage change…"
              value={stageRemarkText}
              onChange={(e) => setStageRemarkText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  confirmStageRemark();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelStageRemark();
                }
              }}
            />
            <div className="stage-remark-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={cancelStageRemark}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmStageRemark}
              >
                Save stage
              </button>
            </div>
          </div>
        </div>
      )}
      {checklistRemarkPrompt && (
        <div
          className="stage-remark-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) cancelChecklistRemarkPrompt();
          }}
        >
          <div className="stage-remark-modal">
            <h4>Add a remark</h4>
            <p className="stage-remark-sub">{checklistRemarkPrompt.title}</p>
            <textarea
              autoFocus
              rows={3}
              placeholder="Add a remark for this checklist item…"
              value={checklistRemarkText}
              onChange={(e) => setChecklistRemarkText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  confirmChecklistRemarkPrompt();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelChecklistRemarkPrompt();
                }
              }}
            />
            <div className="stage-remark-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={cancelChecklistRemarkPrompt}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmChecklistRemarkPrompt}
              >
                Save Remark
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteTarget && (
        <DeleteWarningModal
          message="Are you sure you want to delete this item? This action cannot be undone."
          type="warning"
          onConfirm={() => {
            const { type, id } = deleteTarget;
            if (type === "task" && typeof onDeleteTask === "function")
              onDeleteTask(id);
            if (type === "todo" && typeof onDeleteTodo === "function")
              onDeleteTodo(id);
            if (type === "checklist" && typeof onDeleteTodoList === "function")
              onDeleteTodoList(id);
            setDeleteTarget(null);
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default ListView;
